
// index.tsx
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import App from './App';
import MfaGate from './components/MfaGate';
import OrganizationAccessGate from './components/OrganizationAccessGate';
import { DeliveredReportProvider } from './components/ConsultantReportViewer';
import { isOrganizationAccess } from './common/organizationAccessMode';
import { clearLegacyWorkspaceData } from './common/clientWorkspaceData';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';
import { requireSiteAccess } from './common/siteAccess';
import PaddleCheckoutPage from './components/PaddleCheckoutPage';
import MsaApplicationPage from './components/MsaApplicationPage';
// REMOVE initializeFirebaseApp. ADD db, auth, and functions.
import { db, auth, functions, authSessionReady, legacyPersistenceCleanup } from '../firebaseConfig';
import { setFunctionsInstance } from './common/firebaseServiceLocator';
import '../index.css';

// Inject the functions instance into the service locator.
// This allows shared code to access 'functions' without importing 'firebaseConfig.ts' directly.
if (functions) {
    setFunctionsInstance(functions);
}

const container = document.getElementById('root');
if (!container) {
    throw new Error("Could not find root element to mount to");
}

if (!(container as any)._reactRoot) {
    (container as any)._reactRoot = createRoot(container);
}
const root: Root = (container as any)._reactRoot;
const isPaddleCheckout = window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/checkout';
const signupPath = window.location.pathname.replace(/\/+$/, '').toLowerCase();
if (signupPath === '/msa')
    window.history.replaceState({}, '', `/join${window.location.search}${window.location.hash}`);
const isMsaApplication = signupPath === '/join' || signupPath === '/msa';

// Simple Error Boundary to catch render crashes
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', color: 'red', fontFamily: 'sans-serif' }}>
          <h1>Something went wrong.</h1>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{this.state.error?.toString()}</pre>
          <p>Check the console for more details.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

import { BrowserRouter } from 'react-router-dom';

// --- DEFINITIVE FIX ---
// 1. Wrap the entire app in BrowserRouter to resolve the useLocation() error
// 2. GoogleMapsProvider and ErrorBoundary remain at roots for stability
const application = (
  <BrowserRouter>
    {isPaddleCheckout ? (
      <MfaGate><PaddleCheckoutPage /></MfaGate>
    ) : isMsaApplication ? (
      <MfaGate><MsaApplicationPage /></MfaGate>
    ) : (
      <GoogleMapsProvider>
        {isOrganizationAccess ? <OrganizationAccessGate><MfaGate><DeliveredReportProvider><App /></DeliveredReportProvider></MfaGate></OrganizationAccessGate>
            : <MfaGate><DeliveredReportProvider><App /></DeliveredReportProvider></MfaGate>}
      </GoogleMapsProvider>
    )}
  </BrowserRouter>
);

const startApp = async () => {
  await requireSiteAccess(container!, import.meta.env.VITE_FIREBASE_PROJECT_ID);
  clearLegacyWorkspaceData();
  try {
    await authSessionReady;
  } catch (error) {
    console.error('Could not move management login into this session.', error);
    root.render(<main role="alert" className="p-8">Could not start a secure session. Check browser storage settings, then reload.</main>);
    return;
  }
  let timeoutId: number | undefined;
  try {
    await Promise.race([
      legacyPersistenceCleanup,
      new Promise((_, reject) => { timeoutId = window.setTimeout(() => reject(new Error('Timed out')), 3000); }),
    ]);
  } catch (error) {
    if (!String((error as { code?: string }).code || '').endsWith('unimplemented')) {
      console.error('Could not clear older Firestore browser cache.', error);
      root.render(<main role="alert" className="p-8">Could not clear old browser data. Close other IntelOptics tabs, then reload.</main>);
      return;
    }
  } finally {
    window.clearTimeout(timeoutId);
  }
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        {application}
      </ErrorBoundary>
    </React.StrictMode>
  );
};

void startApp();
